import Head from '@/components/Head/Head';
import { jobDetailsGetExternalLink } from '@/helpers/JobDetails/jobDetailsGetExternalLink';
import transformWorkdayJob from '@/helpers/transformWorkdayJobs';
import useJobFavorites from '@/hooks/useJobFavorites/useJobFavorites';
import useJobs from '@/hooks/useJobs/useJobs';
import useLastUrl from '@/hooks/useLastUrl/useLastUrl';
import type {
  JobCover,
  WorkDayJobPosting,
  WorkDayJobPostingCategory,
} from '@/page-templates/JobDetailsPage/interfaces';
import HeroArea from '@uikit/composites/HeroArea/HeroArea';
import { HeroSize } from '@uikit/composites/HeroArea/consts';
import assetPath from '@uikit/helpers/assetPath';
import getJobDetailsCompanyHelpMessage from '@uikit/helpers/getJobDetailsCompanyHelpMessage';
import { transformWorkdayHtmlAdditionalInfo } from '@uikit/helpers/transformWorkdayHtmlAdditionalInfo';
import { transformWorkdayHtmlExpectations } from '@uikit/helpers/transformWorkdayHtmlExpectations';
import { transformWorkdayHtmlTasks } from '@uikit/helpers/transformWorkdayHtmlTasks';
import { Asset } from '@uikit/types';
import { useState } from 'react';
import Button from '../Button/Button';
import {
  ButtonAlign,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  ShowIcon,
} from '../Button/consts';
import { ClickableAction } from '../Clickable/consts';
import { ClickableProps } from '../Clickable/interfaces';
import Container from '../Container/Container';
import { ContainerChildrenSpace } from '../Container/consts';
import Headline from '../Headline/Headline';
import {
  HeadlineAlign,
  HeadlineColor,
  HeadlineLevel,
} from '../Headline/consts';
import JobContactSection from '../JobContactSection/JobContactSection';
import { JobContact } from '../JobContactSection/interfaces';
import JobDescriptionSection from '../JobDescriptionSection/JobDescriptionSection';
import JobDetailsHybridModal from '../JobDetailsHybridModal/JobDetailsHybridModal';
import JobDetailsOverviewButton from '../JobDetailsOverviewButton/JobDetailsOverviewButton';
import JobHint from '../JobHint/JobHint';
import JobRelatedJobsSection from '../JobRelatedJobsSection/JobRelatedJobsSection';
import JobStageBox from '../JobStageBox/JobStageBox';
import NewHeader from '../NewHeader/NewHeader';
import PageHeader from '../NewHeader/PageHeader/PageHeader';
import ShareAction from '../NewHeader/ShareAction/ShareAction';
import RichText from '../RichText/RichText';
import Section from '../Section/Section';
import { SectionBackgroundColor, SectionVariant } from '../Section/consts';
import Teaser from '../Teaser/Teaser';
import TeaserList from '../TeaserList/TeaserList';
import type {
  JobBenefitsItem,
  JobDetailsPageContentProps,
} from './JobDetailsPageContent.interfaces';
import styles from './JobDetailsPageContent.module.scss';

const APPLY_BUTTON_LABEL = 'Jetzt bewerben';

const getMatchingJobBenefits = (
  jobBenefits: readonly JobBenefitsItem[],
  job: WorkDayJobPosting
) =>
  jobBenefits.filter((jobBenefit) => {
    if (
      job.categories?.[0]?.descriptor &&
      jobBenefit.categories?.some((category) =>
        job.categories?.[0]?.descriptor?.startsWith(category)
      )
    ) {
      return true;
    }

    if (
      job.jobType &&
      jobBenefit.jobType &&
      jobBenefit.jobType.includes(job.jobType.descriptor)
    ) {
      return true;
    }
    return false;
  });

const DEFAULT_JOB_CONTACT: JobContact = {
  email: 'HR-Service@enercity.de',
  firstName: 'Karriere',
  jobTitle: 'enercity',
  lastName: 'Team',
  phone: '0511.430.3195',
  socials: [],
  profileImage: {
    name: 'enercity Logo',
    path: `${assetPath}/assets/logos/enercity_large.jpg`,
  },
  company: [],
};

const getEmailFromWorkdayJson = (html: string) => {
  const strings = ['<h2><b>E-Mail:</b></h2>', '<p><b>E-Mail:</b></p>'];
  let index = -1;
  for (const string of strings) {
    if (html.includes(string)) {
      index = html.indexOf(string) + string.length;
      break;
    }
  }
  if (index === -1) {
    return undefined;
  }
  return html.slice(index);
};

const getMatchingJobContact = (
  jobContacts: readonly JobContact[],
  job: WorkDayJobPosting
) => {
  const email = getEmailFromWorkdayJson(job.jobDescription);
  for (const jobContact of jobContacts) {
    if (jobContact.email === email) {
      return jobContact;
    }
    if (!job.company || !jobContact.company) {
      continue;
    }
    if (jobContact.company.includes(job.company.descriptor)) {
      return jobContact;
    }
  }
  return DEFAULT_JOB_CONTACT;
};

const DEFAULT_JOB_IMAGE: Asset = {
  path: `${assetPath}/assets/images/enercity-karriere-fallback.jpeg`,
  name: '',
};

export const getMatchingJobImage = (
  jobCovers: readonly JobCover[],
  job: WorkDayJobPosting
): Asset => {
  const jobCover = jobCovers.find((jobCover) => {
    if (!job.categories) {
      return false;
    }
    if (jobCover.categories.includes(job.categories[0].descriptor)) {
      return true;
    }
    return false;
  });
  if (!jobCover) {
    return DEFAULT_JOB_IMAGE;
  }
  return jobCover.image;
};

const getRelatedJobsSearchParams = (
  jobCategories: readonly WorkDayJobPostingCategory[]
): string => {
  if (!jobCategories.length) return '';
  const descriptors = jobCategories.map((category) => category.descriptor);
  const searchParams = descriptors.join(',');
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append('arbeitswelten', searchParams);
  return '?' + urlSearchParams.toString();
};

const mergeJobInfos = (...args: (string | undefined)[]) => {
  return args.filter(Boolean).join('') || 'KEINE ANGABE';
};

const JobDetailsPageContent = ({
  jobCovers,
  job,
  timeTypes = [],
  jobBenefits,
  jobContacts,
  children,
  location,
  jobTimeTypeAdditionalInfo,
  deadlineInfo,
  overrideJobCover,
  jobApplicationTarget,
  jobEmailTarget,
  jobExternalTarget,
  relatedJobs,
}: JobDetailsPageContentProps) => {
  const { data: allJobs = [] } = useJobs();
  const knownJobIds = allJobs.map((job) => job.id);
  const matchingJobBenefits = getMatchingJobBenefits(jobBenefits, job);
  const matchingJobContact = getMatchingJobContact(jobContacts, job);
  const matchingJobImage =
    overrideJobCover || getMatchingJobImage(jobCovers, job);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  if (!job) {
    throw new Error('no job found');
  }
  const { isFavoriteJob: isFavorite, toggleFavoriteJob: toggleFavorite } =
    useJobFavorites(new Set(knownJobIds));

  const jobBenefitsList = matchingJobBenefits.map((props, index) => {
    return (
      <Teaser
        key={index}
        isButtonVisible={false}
        isIcon
        icon={props.icon}
        headline={props.name}
        actionType={ClickableAction.None}
      >
        {props.description}
      </Teaser>
    );
  });
  const hasRelatedJobs = relatedJobs.length > 0;
  const relatedSearchParams = getRelatedJobsSearchParams(job.categories || []);

  const isFixedTerm = job.jobType?.descriptor.includes('(Fixed Term)');

  const fixedTermLabel = isFixedTerm ? ' | befristet' : '';

  const hasEmailTarget = jobApplicationTarget === 'email' && jobEmailTarget;
  const hasHybridTarget =
    jobApplicationTarget === 'hybridWorkday' && jobEmailTarget;

  const timeType = mergeJobInfos(
    timeTypes.join(' / '),
    fixedTermLabel,
    jobTimeTypeAdditionalInfo
  );

  const helperText = getJobDetailsCompanyHelpMessage(
    job.company?.descriptor ?? ''
  );

  const externalLink = jobDetailsGetExternalLink(
    jobApplicationTarget,
    jobExternalTarget,
    job.url
  );

  const getPageHeaderProps = () => {
    if (hasHybridTarget) {
      return {
        actionType: ClickableAction.Custom,
        onClick: handleModalOpen,
      };
    }

    if (hasEmailTarget) {
      return {
        actionType: ClickableAction.MailTo,
        email: jobEmailTarget,
      };
    }

    return {
      actionType: ClickableAction.Linkextern,
      linkextern: externalLink,
    };
  };

  const transformedRelatedJobs = relatedJobs.map(transformWorkdayJob);

  const additionalInfo = transformWorkdayHtmlAdditionalInfo(job.jobDescription);

  const { getLastUrl } = useLastUrl();
  const lastOverviewUrl = getLastUrl(
    (url) =>
      url.includes('/karriere/jobsuche?') || url.endsWith('/karriere/jobsuche')
  );

  const jobIsFavorited = isFavorite && !!job.id && isFavorite(job.id);

  return (
    <>
      <Head>
        <meta name="workdayId" content={job.id} />
      </Head>

      <NewHeader
        hasPageHeader
        hasPreHeader={false}
        renderPageHeader={(props) => (
          <PageHeader
            {...props}
            addonsSlot={
              <>
                <PageHeader.Action
                  aria-label={
                    jobIsFavorited
                      ? 'Aus Favoriten entfernen'
                      : 'Zu Favoriten hinzufügen'
                  }
                  icon={jobIsFavorited ? 'action/heart-filled' : 'action/heart'}
                  isActive={jobIsFavorited}
                  onClick={() => toggleFavorite(job.id)}
                  title={jobIsFavorited ? 'Gemerkt' : 'Merken'}
                />

                <ShareAction
                  headline="Dieses Jobangebot teilen"
                  text={job.title || 'KEINE ANGABE'}
                  title={job.title || 'KEINE ANGABE'}
                />

                <Button
                  {...(getPageHeaderProps() as ClickableProps)}
                  size={ButtonSize.Small}
                >
                  {APPLY_BUTTON_LABEL}
                </Button>
              </>
            }
            renderTitle={() => (
              <JobDetailsOverviewButton
                url={lastOverviewUrl ?? '/karriere/jobsuche'}
              />
            )}
          />
        )}
        variant="career"
      />

      <HeroArea
        image={matchingJobImage}
        size={HeroSize.Large}
        skewedBottom={true}
        hasAspectRatioFallback={true}
        stageBoxClassName={styles.stageBox}
      >
        <JobStageBox
          title={job.title || 'KEINE ANGABE'}
          jobId={job.jobId}
          primaryLocation={location || 'KEINE ANGABE'}
          timeType={timeType}
          helperText={helperText}
          deadlineInfo={deadlineInfo}
          url={externalLink}
          openModal={handleModalOpen}
          jobOverviewUrl="#"
          jobApplicationTarget={jobApplicationTarget}
          jobEmailTarget={jobEmailTarget}
        />
      </HeroArea>

      <JobDescriptionSection
        hint={<JobHint />}
        items={[
          <RichText key="tasks">
            {transformWorkdayHtmlTasks(job.jobDescription)}
          </RichText>,
          <RichText key="expectations">
            {transformWorkdayHtmlExpectations(job.jobDescription)}
          </RichText>,
        ]}
        additionalInfo={additionalInfo && <RichText>{additionalInfo}</RichText>}
      />

      {children}
      <Section
        bgColor={SectionBackgroundColor.Violet}
        variant={SectionVariant.LargePadding}
      >
        <Container spaceBetweenChildren={ContainerChildrenSpace.Mini}>
          <Headline
            headlineColor={HeadlineColor.Gradient}
            level={HeadlineLevel.Two}
            align={HeadlineAlign.Left}
            isCustomWidth={true}
            className={styles.benefitsHeadline}
          >
            Das Sahnehäubchen – diese Vorteile bieten wir dir:
          </Headline>
          <TeaserList>{jobBenefitsList}</TeaserList>
          <Button
            align={ButtonAlign.Center}
            color={ButtonColor.Primary}
            size={ButtonSize.Medium}
            width={ButtonWidth.Auto}
            showIcon={ShowIcon.None}
            actionType={ClickableAction.Linkextern}
            linkextern={'/karriere/warum-enercity/benefits'}
          >
            Alle Vorteile kennenlernen
          </Button>
        </Container>
      </Section>
      <Section variant={SectionVariant.LargePadding}>
        <Container spaceBetweenChildren={ContainerChildrenSpace.Mini}>
          <Headline
            headlineColor={HeadlineColor.Gradient}
            level={HeadlineLevel.Two}
            align={HeadlineAlign.Left}
            isCustomWidth={true}
            subText="Für uns steht an erster Stelle, dass alle Mitarbeiter:innen ihre
            Potenziale leben können. Dafür bieten wir ihnen ein Umfeld, das
            vielschichtige Möglichkeiten eröffnet. Netter Nebeneffekt: Wir
            erhalten Siegel, die uns das bestätigen."
          >
            Wir sind ausgezeichnet!
          </Headline>

          <div className={styles.awards}>
            <img
              src="/assets/jobs/seals/logo-berufundfamilie-2010.svg"
              alt="logo-berufundfamilie-2010"
            />
            <img
              src="/assets/jobs/seals/logo-top-employer-2024.webp"
              alt="logo-top-employer-2023"
            />
            <img
              src="/assets/jobs/seals/logo-zukunftsfest-2022.svg"
              alt="logo-zukunftsfest-2022"
            />
            <img
              src="/assets/jobs/seals/logo-kununu-top-company-2024.svg"
              alt="logo-kununu-top-company-2023"
            />
          </div>
        </Container>
      </Section>
      <Section
        variant={SectionVariant.LargePadding}
        bgColor={SectionBackgroundColor.Gradient}
        className={styles.interestSection}
      >
        <Container spaceBetweenChildren={ContainerChildrenSpace.Small}>
          <Headline
            className={styles.applyHeadline}
            headlineColor={HeadlineColor.Light}
            level={HeadlineLevel.Two}
            align={HeadlineAlign.Center}
            isCustomWidth={true}
            subText={
              'Dann fackel nicht lange und schick uns deine Bewerbung. Nutze dafür einfach unser Online-Bewerbungsformular.'
            }
            spacing="large"
          >
            Interesse geweckt?
          </Headline>

          {hasEmailTarget ? (
            <Button
              color={ButtonColor.Secondary}
              align={ButtonAlign.Center}
              actionType={ClickableAction.MailTo}
              email={jobEmailTarget}
            >
              {APPLY_BUTTON_LABEL}
            </Button>
          ) : hasHybridTarget ? (
            <Button
              color={ButtonColor.Secondary}
              align={ButtonAlign.Center}
              actionType={ClickableAction.Custom}
              onClick={handleModalOpen}
            >
              {APPLY_BUTTON_LABEL}
            </Button>
          ) : (
            <Button
              color={ButtonColor.Secondary}
              align={ButtonAlign.Center}
              actionType={ClickableAction.Linkextern}
              linkextern={externalLink}
            >
              {APPLY_BUTTON_LABEL}
            </Button>
          )}
        </Container>
      </Section>

      <JobContactSection
        headline="Fragen zur Bewerbung?"
        subText="Meld dich gerne bei mir!"
        contactPerson={matchingJobContact}
      />

      {hasRelatedJobs && (
        <JobRelatedJobsSection
          jobs={transformedRelatedJobs}
          headline="Mehr Jobs aus diesem Bereich entdecken"
          searchParams={relatedSearchParams}
          isFavorite={isFavorite}
          toggleFavorite={toggleFavorite}
        />
      )}

      <JobDetailsHybridModal
        isOpen={isModalOpen}
        handleClose={handleModalClose}
        emailTarget={jobEmailTarget}
        externalLink={externalLink}
      />
    </>
  );
};

export default JobDetailsPageContent;
